import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, Upload, Paperclip, X, Users, Plus, Minus, Trash2 } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import { CouponBox, couponDiscount } from "./CouponBox";

/**
 * Public form page. No auth. Loads a published form by slug and lets users
 * submit + pay via Razorpay Checkout script (loaded on demand).
 */
export default function PublicFormPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitter, setSubmitter] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {status, message}
  const [error, setError] = useState(null);
  const rzpScriptLoaded = useRef(false);

  // ---- Bulk registration state ----
  // A group registrant enters N delegates in a single submission. Each row
  // captures: name, email, phone, committee_id, portfolio (dropdowns from
  // the event's committee matrix). Total = form.price_paise × delegates.length.
  const [bulkMode, setBulkMode] = useState(false);
  const [delegates, setDelegates] = useState([blankDelegate()]);

  const setDelegate = (idx, patch) => setDelegates((arr) => arr.map((d, i) => i === idx ? { ...d, ...patch } : d));
  const addDelegate = () => setDelegates((arr) => {
    const max = (form && form.max_bulk_count) || 30;
    if (arr.length >= max) { toast.error(`Maximum ${max} delegates per submission`); return arr; }
    return [...arr, blankDelegate()];
  });
  const removeDelegate = (idx) => setDelegates((arr) => arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr);

  const [coupon, setCoupon] = useState(null);
  const payCount = bulkMode ? delegates.length : 1;
  const discountPaise = couponDiscount(coupon, form?.price_paise || 0, payCount);
  const payTotalPaise = (form?.price_paise || 0) * payCount - discountPaise;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/public/forms/${slug}`);
        if (alive) setForm(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Form not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const setAns = (fid, v) => setAnswers((a) => ({ ...a, [fid]: v }));

  const loadRazorpayScript = () => new Promise((resolve, reject) => {
    if (rzpScriptLoaded.current || window.Razorpay) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => { rzpScriptLoaded.current = true; resolve(); };
    s.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(s);
  });

  const uploadFile = async (fid, file, maxMb) => {
    if (!file) return;
    if (file.size > (maxMb || 10) * 1024 * 1024) return toast.error(`File exceeds ${maxMb} MB limit`);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("form_slug", slug);
    try {
      const r = await api.post("/public/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAns(fid, { url: r.data.url, name: r.data.name || file.name, size: r.data.size });
      toast.success("Attached");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (bulkMode) {
      // Bulk-mode validation client-side before the server round-trip.
      for (let i = 0; i < delegates.length; i++) {
        const d = delegates[i];
        if (!d.name.trim() || !d.email.trim() || !d.phone.trim())
          return toast.error(`Delegate #${i + 1}: name, email and phone are required`);
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email))
          return toast.error(`Delegate #${i + 1}: invalid email`);
        if (!d.committee_id)
          return toast.error(`Delegate #${i + 1}: pick a committee`);
      }
    } else {
      if (!submitter.name.trim() || !submitter.email.trim()) return toast.error("Name and email are required");
      for (const f of (form.fields || [])) {
        if (f.required) {
          const v = answers[f.field_id];
          if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) return toast.error(`"${f.label}" is required`);
        }
      }
    }
    setSubmitting(true);
    try {
      const body = bulkMode
        ? { delegates: delegates.map((d) => ({
            name: d.name.trim(), email: d.email.trim(), phone: d.phone.trim(),
            committee_id: d.committee_id, portfolio: d.portfolio,
          })) }
        : {
            submitter_name: submitter.name,
            submitter_email: submitter.email,
            submitter_phone: submitter.phone,
            answers,
          };
      if (coupon && discountPaise > 0) body.coupon_code = coupon.code;
      const { data } = await api.post(`/public/forms/${slug}/submit`, body);
      if (!data.requires_payment) {
        setDone({ status: "paid", message: data.success_message });
        return;
      }
      await loadRazorpayScript();
      const opts = {
        key: data.order.key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        order_id: data.order.order_id,
        name: form.event?.name || form.name,
        description: form.name,
        image: form.event?.logo_url || undefined,
        prefill: {
          name: bulkMode ? delegates[0]?.name : submitter.name,
          email: bulkMode ? delegates[0]?.email : submitter.email,
          contact: bulkMode ? delegates[0]?.phone : submitter.phone,
        },
        theme: { color: "#F97316" },
        handler: async (res) => {
          try {
            const v = await api.post(`/public/forms/${slug}/verify-payment`, {
              submission_id: data.submission_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            if (v.data.ok) setDone({ status: "paid", message: v.data.success_message });
            else setDone({ status: "failed", message: "Payment verification failed. Please contact support." });
          } catch {
            setDone({ status: "failed", message: "Payment verification failed. Please contact support." });
          }
        },
        modal: {
          ondismiss: async () => {
            await api.post(`/public/forms/${slug}/mark-failed`, { submission_id: data.submission_id, reason: "User closed checkout" }).catch(() => {});
            toast.error("Payment cancelled");
          },
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", async (resp) => {
        await api.post(`/public/forms/${slug}/mark-failed`, { submission_id: data.submission_id, reason: resp?.error?.description || "Payment failed" }).catch(() => {});
        setDone({ status: "failed", message: resp?.error?.description || "Payment failed. Please try again." });
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  if (error) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="bg-white rounded-2xl p-10 max-w-md text-center shadow-lg" data-testid="public-form-error">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
        <h1 className="font-display font-black text-2xl">Form unavailable</h1>
        <p className="text-slate-500 mt-2 text-sm">{error}</p>
      </div>
    </div>
  );
  if (done) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="public-form-done">
      <div className={`bg-white rounded-2xl p-10 max-w-md text-center shadow-lg ${done.status === "paid" ? "border-emerald-200" : "border-rose-200"} border-2`}>
        {done.status === "paid" ? <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" /> : <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto mb-3" />}
        <h1 className="font-display font-black text-2xl">{done.status === "paid" ? "You're in!" : "Payment failed"}</h1>
        <p className="text-slate-600 mt-2 whitespace-pre-wrap">{done.message}</p>
        <p className="text-[10px] text-slate-400 mt-4">Form: {form.name}</p>
      </div>
    </div>
  );

  const banner = form.header_banner_url ? (form.header_banner_url.startsWith("http") ? form.header_banner_url : `${BACKEND_URL}${form.header_banner_url}`) : null;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="max-w-2xl mx-auto pt-4 sm:pt-10 px-4">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden" data-testid="public-form">
          {banner && (
            <div className="w-full aspect-[3/1] bg-slate-100 relative overflow-hidden">
              <img src={banner} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
          )}
          <div className="p-6 sm:p-10">
            <div className="text-[10px] uppercase font-bold tracking-wider text-orange-600">{form.event?.name || "Registration"}</div>
            <h1 className="font-display font-black text-3xl mt-1" data-testid="public-form-name">{form.name}</h1>
            {form.description && <p className="text-slate-600 mt-3 whitespace-pre-wrap">{form.description}</p>}
            {form.price_paise > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 text-sm font-bold">
                Registration fee · ₹{(form.price_paise / 100).toLocaleString("en-IN")}
              </div>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4">
              {/* Bulk toggle appears only when the form allows it AND has a per-user price */}
              {form.allow_bulk && form.price_paise > 0 && (
                <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4" data-testid="pf-bulk-panel">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-2.5">
                      <Users className="w-5 h-5 text-sky-600 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">Registering multiple delegates?</div>
                        <div className="text-xs text-slate-500 mt-0.5">Add up to {form.max_bulk_count} delegates in one submission. You&apos;ll pay the total for all at once.</div>
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold" data-testid="pf-bulk-toggle">
                      <input type="checkbox" checked={bulkMode} onChange={(e) => setBulkMode(e.target.checked)} className="w-4 h-4 accent-sky-600" />
                      Enable bulk registration
                    </label>
                  </div>
                </div>
              )}

              {bulkMode ? (
                <BulkDelegatesSection
                  form={form}
                  delegates={delegates}
                  setDelegate={setDelegate}
                  addDelegate={addDelegate}
                  removeDelegate={removeDelegate}
                />
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input required placeholder="Full name *" className="input-pill" value={submitter.name} onChange={(e) => setSubmitter({ ...submitter, name: e.target.value })} data-testid="pf-name" />
                    <input required type="email" placeholder="Email *" className="input-pill" value={submitter.email} onChange={(e) => setSubmitter({ ...submitter, email: e.target.value })} data-testid="pf-email" />
                  </div>
                  <input placeholder="Phone (WhatsApp)" className="input-pill" value={submitter.phone} onChange={(e) => setSubmitter({ ...submitter, phone: e.target.value })} data-testid="pf-phone" />

                  <div className="pt-3 border-t border-slate-100 space-y-4">
                    {(form.fields || []).map((f) => (
                      <FieldRenderer key={f.field_id} field={f} value={answers[f.field_id]} onChange={(v) => setAns(f.field_id, v)} onFileUpload={(file) => uploadFile(f.field_id, file, f.max_size_mb || 10)} />
                    ))}
                  </div>
                </>
              )}

              {form.price_paise > 0 && (
                <div className="space-y-2">
                  <CouponBox slug={slug} count={payCount} coupon={coupon} setCoupon={setCoupon} />
                  {discountPaise > 0 && (
                    <div className="text-sm text-slate-600 flex justify-between px-1" data-testid="pf-coupon-summary">
                      <span>Discount ({coupon.code})</span>
                      <span className="font-bold text-emerald-700">−₹{(discountPaise / 100).toLocaleString("en-IN")}</span>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" disabled={submitting} className="btn-primary w-full py-3 disabled:opacity-50" data-testid="pf-submit">
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Working…</>
                ) : bulkMode ? (
                  `Pay ₹${(payTotalPaise / 100).toLocaleString("en-IN")} for ${delegates.length} delegate${delegates.length > 1 ? "s" : ""} & Submit`
                ) : form.price_paise > 0 ? (
                  payTotalPaise > 0 ? `Pay ₹${(payTotalPaise / 100).toLocaleString("en-IN")} & Submit` : "Submit"
                ) : "Submit"}
              </button>
              <p className="text-[10px] text-slate-400 text-center">Powered by MUNner&apos;s · Secure payments via Razorpay</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, onFileUpload }) {
  const label = (
    <label className="text-xs font-semibold text-slate-700 block mb-1">
      {field.label} {field.required && <span className="text-rose-500">*</span>}
    </label>
  );
  const testid = `pf-field-${field.field_id}`;

  switch (field.type) {
    case "short_text":
      return <div>{label}<input className="input-pill" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case "long_text":
      return <div>{label}<textarea rows={4} className="input-pill resize-none" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case "number":
      return <div>{label}<input type="number" className="input-pill" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case "email":
      return <div>{label}<input type="email" className="input-pill" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case "phone":
      return <div>{label}<input type="tel" className="input-pill" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case "date":
      return <div>{label}<input type="date" className="input-pill" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case "dropdown":
      return (
        <div>{label}
          <select className="input-pill" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
            <option value="">— Choose —</option>
            {(field.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
          </select>
        </div>
      );
    case "radio":
      return (
        <div>{label}
          <div className="space-y-1.5">
            {(field.options || []).map((o, i) => (
              <label key={i} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="radio" name={field.field_id} checked={value === o} onChange={() => onChange(o)} data-testid={`${testid}-opt-${i}`} />
                {o}
              </label>
            ))}
          </div>
        </div>
      );
    case "checkbox":
      return (
        <div>{label}
          <div className="space-y-1.5">
            {(field.options || []).map((o, i) => {
              const arr = Array.isArray(value) ? value : [];
              const on = arr.includes(o);
              return (
                <label key={i} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={on} onChange={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])} data-testid={`${testid}-opt-${i}`} />
                  {o}
                </label>
              );
            })}
          </div>
        </div>
      );
    case "file": {
      const cur = value;
      const accept = "application/pdf,image/jpeg,image/png,image/webp";
      return (
        <div>{label}
          {cur?.url ? (
            <div className="flex items-center gap-2 p-2 rounded-xl border border-emerald-200 bg-emerald-50">
              <Paperclip className="w-4 h-4 text-emerald-600" />
              <a href={cur.url.startsWith("http") ? cur.url : `${BACKEND_URL}${cur.url}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline flex-1 truncate">{cur.name}</a>
              <button type="button" onClick={() => onChange(null)} className="text-rose-500"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <label className="btn-ghost cursor-pointer w-full py-3 justify-center border-2 border-dashed border-slate-200 hover:border-orange-300" data-testid={testid}>
              <Upload className="w-4 h-4" />
              <span className="text-sm">Upload PDF / JPG / PNG (max {field.max_size_mb || 10} MB)</span>
              <input type="file" accept={accept} className="hidden" onChange={(e) => onFileUpload(e.target.files?.[0])} />
            </label>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}


// -------- Bulk-registration helpers ------------------------------------------
function blankDelegate() {
  return { name: "", email: "", phone: "", committee_id: "", portfolio: "" };
}

function BulkDelegatesSection({ form, delegates, setDelegate, addDelegate, removeDelegate }) {
  const committees = form.committees || [];
  const perDelegateFee = (form.price_paise || 0) / 100;
  return (
    <div className="space-y-3" data-testid="pf-bulk-list">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-800">Delegates ({delegates.length})</div>
        <div className="text-xs text-slate-500">
          ₹{perDelegateFee.toLocaleString("en-IN")} × {delegates.length} = <span className="font-bold text-emerald-700 text-sm">₹{(perDelegateFee * delegates.length).toLocaleString("en-IN")}</span>
        </div>
      </div>

      {delegates.map((d, idx) => {
        const com = committees.find((c) => c.committee_id === d.committee_id);
        const portfolios = com?.portfolios || [];
        return (
          <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2.5" data-testid={`pf-bulk-row-${idx}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-slate-500">Delegate #{idx + 1}</div>
              {delegates.length > 1 && (
                <button type="button" onClick={() => removeDelegate(idx)} className="text-rose-500 hover:bg-rose-50 rounded p-1" title="Remove" data-testid={`pf-bulk-remove-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              <input required placeholder="Full name *" className="input-pill" value={d.name}
                     onChange={(e) => setDelegate(idx, { name: e.target.value })}
                     data-testid={`pf-bulk-name-${idx}`} />
              <input required type="email" placeholder="Email *" className="input-pill" value={d.email}
                     onChange={(e) => setDelegate(idx, { email: e.target.value })}
                     data-testid={`pf-bulk-email-${idx}`} />
            </div>
            <input required placeholder="Phone (WhatsApp) *" className="input-pill" value={d.phone}
                   onChange={(e) => setDelegate(idx, { phone: e.target.value })}
                   data-testid={`pf-bulk-phone-${idx}`} />
            <div className="grid sm:grid-cols-2 gap-2.5">
              <select className="input-pill" value={d.committee_id}
                      onChange={(e) => setDelegate(idx, { committee_id: e.target.value, portfolio: "" })}
                      data-testid={`pf-bulk-committee-${idx}`}>
                <option value="">— Choose committee * —</option>
                {committees.map((c) => (
                  <option key={c.committee_id} value={c.committee_id}>{c.name}</option>
                ))}
              </select>
              <select className="input-pill" value={d.portfolio}
                      onChange={(e) => setDelegate(idx, { portfolio: e.target.value })}
                      disabled={!d.committee_id}
                      data-testid={`pf-bulk-portfolio-${idx}`}>
                <option value="">— Portfolio preference —</option>
                {portfolios.map((p, pi) => <option key={pi} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={addDelegate} className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="pf-bulk-add">
          <Plus className="w-4 h-4" /> Add another delegate
        </button>
        <div className="text-[10px] text-slate-400">Max {form.max_bulk_count} per submission</div>
      </div>
    </div>
  );
}
